import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { submitContact } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { QUOTES } from "@/lib/quotes";
import { toast } from "sonner";
import { useSEO, SEO } from "@/lib/seo";
import { useLiveAnnounce, LiveRegion } from "@/lib/a11y";

const ENQUIRY_TYPES = [
    { value: "general", label: "General enquiry" },
    { value: "media", label: "Media & podcast" },
    { value: "speaking", label: "Speaking engagement" },
    { value: "valanti_labs", label: "Valanti Labs partnership" },
    { value: "alati", label: "ALATI enquiry" },
    { value: "music", label: "Music enquiry" },
];

export default function Contact() {
    useSEO(SEO.contact);
    const { message: liveMessage, announce } = useLiveAnnounce();
    const [form, setForm] = useState({
        name: "",
        email: "",
        organisation: "",
        enquiry_type: "general",
        message: "",
        consent: false,
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const onChange = (k) => (e) => {
        const v = k === "consent" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [k]: v }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.consent) {
            const msg = "Please confirm your consent before sending.";
            setError(msg);
            announce(msg);
            return;
        }
        setLoading(true);
        try {
            await submitContact(form);
            setDone(true);
            toast.success("Thank you. Your message has been received.");
            announce("Thank you. Your message has been received.");
            setForm({
                name: "",
                email: "",
                organisation: "",
                enquiry_type: "general",
                message: "",
                consent: false,
            });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            setError(msg);
            toast.error(msg);
            announce(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main>
            <section className="bg-ivory text-charcoal pt-36 md:pt-44 pb-12 md:pb-16">
                <div className="container-editorial">
                    <p className="eyebrow text-oxblood">Contact</p>
                    <h1 className="display-1 mt-8 max-w-4xl">
                        Perhaps this is where <span className="italic">something meaningful begins.</span>
                    </h1>
                </div>
            </section>

            <section className="bg-ivory">
                <div className="container-editorial py-12 md:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        {/* Left — pathways */}
                        <div className="lg:col-span-5">
                            <p className="eyebrow text-stone mb-6">Pathways</p>
                            <ul className="space-y-6">
                                {ENQUIRY_TYPES.map((t) => (
                                    <li key={t.value} className="border-t border-charcoal/10 pt-5">
                                        <p className="font-serif text-2xl md:text-3xl">{t.label}</p>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-16 pl-6 border-l-2 border-gold">
                                <p className="font-serif italic text-xl md:text-2xl leading-snug text-charcoal">
                                    {QUOTES.readerInvitation}
                                </p>
                            </div>
                        </div>

                        {/* Right — form */}
                        <div className="lg:col-span-7">
                            {done ? (
                                <div
                                    data-testid={TID.contact.success}
                                    className="p-10 border border-gold/60 bg-ivory-soft"
                                >
                                    <p className="eyebrow text-oxblood">Thank you</p>
                                    <h2 className="display-3 mt-4">Your message has been received.</h2>
                                    <p className="mt-6 body-base text-charcoal/70">
                                        I read everything personally. You will hear back from me directly. Please allow a
                                        little time — quiet replies are still replies.
                                    </p>
                                </div>
                            ) : (
                                <form
                                    data-testid={TID.contact.form}
                                    onSubmit={onSubmit}
                                    className="space-y-8"
                                    noValidate
                                >
                                    <Field
                                        label="Your name"
                                        id="contact-name"
                                        testid={TID.contact.name}
                                        value={form.name}
                                        onChange={onChange("name")}
                                        required
                                    />
                                    <Field
                                        label="Email address"
                                        id="contact-email"
                                        testid={TID.contact.email}
                                        type="email"
                                        value={form.email}
                                        onChange={onChange("email")}
                                        required
                                    />
                                    <Field
                                        label="Organisation (optional)"
                                        id="contact-org"
                                        testid={TID.contact.org}
                                        value={form.organisation}
                                        onChange={onChange("organisation")}
                                    />

                                    <div>
                                        <label htmlFor="contact-type" className="eyebrow text-stone">
                                            Enquiry type
                                        </label>
                                        <select
                                            id="contact-type"
                                            data-testid={TID.contact.type}
                                            value={form.enquiry_type}
                                            onChange={onChange("enquiry_type")}
                                            className="input-line mt-2 cursor-pointer"
                                        >
                                            {ENQUIRY_TYPES.map((t) => (
                                                <option key={t.value} value={t.value}>
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="contact-message" className="eyebrow text-stone">
                                            Your message
                                        </label>
                                        <textarea
                                            id="contact-message"
                                            data-testid={TID.contact.message}
                                            rows={6}
                                            value={form.message}
                                            onChange={onChange("message")}
                                            required
                                            placeholder="Tell me a little about what brings you here."
                                            className="input-line mt-2 resize-none"
                                        />
                                    </div>

                                    <label
                                        htmlFor="contact-consent"
                                        className="flex items-start gap-4 cursor-pointer"
                                    >
                                        <input
                                            id="contact-consent"
                                            type="checkbox"
                                            data-testid={TID.contact.consent}
                                            checked={form.consent}
                                            onChange={onChange("consent")}
                                            className="mt-1 w-4 h-4 accent-oxblood"
                                        />
                                        <span className="text-sm text-charcoal/75">
                                            I consent to Chrissa Valanti storing the details of this enquiry for the
                                            purpose of replying. No tracking pixels or third-party sharing.
                                        </span>
                                    </label>

                                    {error && (
                                        <p data-testid={TID.contact.error} className="text-sm text-oxblood">
                                            {error}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid={TID.contact.submit}
                                        className="btn-primary"
                                    >
                                        {loading ? "Sending…" : "Send enquiry"}
                                        <ArrowRight size={16} strokeWidth={1.5} />
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

function Field({ label, id, testid, type = "text", value, onChange, required }) {
    return (
        <div>
            <label htmlFor={id} className="eyebrow text-stone">
                {label}
            </label>
            <input
                id={id}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testid}
                className="input-line mt-2"
            />
        </div>
    );
}
